/**
 * Records which link brought a visitor to Tree Tier, so that if they register — now, or after
 * closing the tab and coming back on Thursday — we know what to credit.
 *
 * First-party and cookie-less by design: no third party ever sees this, it is never sent anywhere
 * except to our own API at registration, and it is cleared the moment it has been used.
 */

import { API_BASE_URL } from "api/config";

/** One arrival: the campaign parameters on the link, where it came from, and when. */
export type AcquisitionTouch = {
	utmSource?: string;
	utmMedium?: string;
	utmCampaign?: string;
	utmContent?: string;
	utmTerm?: string;
	gclid?: string;
	fbclid?: string;
	referrer?: string;
	landingPath?: string;
	occurredAtUtc?: string;
};

/** What the register and Google sign-in calls send to the API. */
export type AcquisitionMetadata = {
	firstTouch?: AcquisitionTouch;
	lastTouch?: AcquisitionTouch;
	timeZone?: string;
};

/** Write-once: the visit that introduced this person to Tree Tier. This is what gets attributed. */
const FIRST_TOUCH_KEY = "tt_first_touch";

/** Always overwritten: the most recent arrival. Diagnostic only. */
const LAST_TOUCH_KEY = "tt_last_touch";

/**
 * Longest value kept for any one field. The API caps these too; trimming here keeps a pathological
 * tracking link from filling the visitor's storage quota.
 */
const MAX_FIELD_LENGTH = 512;

const clamp = (value: string | null): string | undefined => {
	const trimmed = value?.trim();

	if (!trimmed) {
		return undefined;
	}

	return trimmed.length <= MAX_FIELD_LENGTH ? trimmed : trimmed.slice(0, MAX_FIELD_LENGTH);
};

/**
 * localStorage throws rather than no-ops in some privacy modes and embedded webviews. Attribution is
 * diagnostic, so every access fails closed to "we don't know" and never to a broken page.
 */
const readKey = (key: string): AcquisitionTouch | undefined => {
	try {
		const raw = window.localStorage.getItem(key);

		return raw ? (JSON.parse(raw) as AcquisitionTouch) : undefined;
	} catch {
		return undefined;
	}
};

const writeKey = (key: string, touch: AcquisitionTouch): void => {
	try {
		window.localStorage.setItem(key, JSON.stringify(touch));
	} catch {
		// Storage unavailable or full. The visit is simply unattributed.
	}
};

/**
 * Hosts that are us. The API is on this list because the share link people paste lives there: it
 * serves the Open Graph tags a link preview needs and then redirects here, so `document.referrer` on
 * arrival is the API. Without this, every visitor who followed a shared tree link would be recorded
 * as an external referral from our own infrastructure — and because that counts as signal, it would
 * be written as the write-once first touch and displace the real source for good.
 */
const getInternalHosts = (): string[] => {
	const hosts = [window.location.host];

	try {
		hosts.push(new URL(API_BASE_URL).host);
	} catch {
		// A relative or malformed API base leaves just our own host, which is the previous behaviour.
	}

	return hosts;
};

/**
 * True when the referrer is a genuinely external site. Our own pages are not a referral — without
 * this, an internal navigation would overwrite the real source with "treetier.com".
 */
const isExternalReferrer = (referrer: string): boolean => {
	if (!referrer) {
		return false;
	}

	try {
		return !getInternalHosts().includes(new URL(referrer).host);
	} catch {
		return false;
	}
};

/**
 * Builds a touch from the current URL, or returns null when this arrival carries no signal at all —
 * no campaign parameters and no external referrer. Returning null is what keeps an internal
 * navigation from overwriting the real first touch with an empty one.
 */
const readCurrentTouch = (): AcquisitionTouch | null => {
	const params = new URLSearchParams(window.location.search);
	const referrer = isExternalReferrer(document.referrer) ? document.referrer : "";

	const touch: AcquisitionTouch = {
		fbclid: clamp(params.get("fbclid")),
		gclid: clamp(params.get("gclid")),
		landingPath: clamp(window.location.pathname),
		occurredAtUtc: new Date().toISOString(),
		referrer: clamp(referrer),
		utmCampaign: clamp(params.get("utm_campaign")),
		utmContent: clamp(params.get("utm_content")),
		utmMedium: clamp(params.get("utm_medium")),
		utmSource: clamp(params.get("utm_source")),
		utmTerm: clamp(params.get("utm_term"))
	};

	const hasSignal = Boolean(
		touch.utmSource ?? touch.utmMedium ?? touch.utmCampaign ?? touch.gclid ?? touch.fbclid ?? touch.referrer
	);

	return hasSignal ? touch : null;
};

/**
 * Called on every route change. Writes the first touch once and the last touch every time.
 *
 * ⚠️ WhatsApp, Telegram and Signal send no referrer header, so an untagged link shared there is
 * indistinguishable from someone typing the URL and will be recorded as "direct". Nothing here can
 * recover it — the fix is to put a utm_source on every link before it is posted.
 */
export const captureTouch = (): void => {
	const touch = readCurrentTouch();

	if (!touch) {
		return;
	}

	if (!readKey(FIRST_TOUCH_KEY)) {
		writeKey(FIRST_TOUCH_KEY, touch);
	}

	writeKey(LAST_TOUCH_KEY, touch);
};

/** The browser's IANA time zone, or undefined when it cannot be read. */
const getTimeZone = (): string | undefined => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
	} catch {
		return undefined;
	}
};

/**
 * What to send with a registration. Returns undefined when nothing was ever captured, so a direct
 * signup sends no attribution field at all rather than an object full of nulls.
 */
export const readAcquisitionMetadata = (): AcquisitionMetadata | undefined => {
	const firstTouch = readKey(FIRST_TOUCH_KEY);
	const lastTouch = readKey(LAST_TOUCH_KEY);
	const timeZone = getTimeZone();

	if (!firstTouch && !lastTouch) {
		return timeZone ? { timeZone } : undefined;
	}

	return { firstTouch, lastTouch, timeZone };
};

/**
 * Clears the stash after a successful registration. Without this a second account created on the
 * same device would inherit the first one's attribution, which is how one Reel ends up credited
 * with a family's worth of signups.
 */
export const clearAcquisitionMetadata = (): void => {
	try {
		window.localStorage.removeItem(FIRST_TOUCH_KEY);
		window.localStorage.removeItem(LAST_TOUCH_KEY);
	} catch {
		// Nothing to clear if storage was never available.
	}
};
